import { atomWithStorage } from "jotai/utils";
import { SlackIcon } from "lucide-react";

import AovAnalytics from "src/components/Analytics/AovAnalytics/AovAnalytics";
import { BreakEvenAnalysis } from "src/components/Analytics/BreakEvenAnalysis/BreakEvenAnalysis";
import CohortAnalytics from "src/components/Analytics/CohortAnalytics/CohortAnalytics";
import MarketingActivity from "src/components/Analytics/MarketingActivity/MarketingActivity";
import NormalizedPL from "src/components/Analytics/NormalizedPL/NormalizedPL";
import UnitEconomics from "src/components/Analytics/UnitEconomics/UnitEconomics";
import Dashboard from "src/components/Dashboard/Dashboard";
import { GoogleAdsIcon } from "src/components/icon/GoogleAdsIcon";
import { MetaIcon } from "src/components/icon/MetaIcon";
import { ShopifyIcon } from "src/components/icon/ShopifyIcon";
import { TikTokIcon } from "src/components/icon/TikTokIcon";
import {
  googleMarketingActivityAtom,
  metaMarketingActivityAtom,
} from "src/lib/hooks/useAnalytics";

export const DEFAULT_TRIAL_DAYS = 7;

export const storeIntegrationTypes = {
  shopify: {
    id: "shopify",
    name: "Shopify",
    nameLong: "Shopify store",
    icon: ShopifyIcon,
    selectedAtom: atomWithStorage("selectedShopifyStore", null),
  },
};

export const marketingIntegrationTypes = {
  meta_ads: {
    id: "meta_ads",
    name: "Meta ads",
    nameLong: "Meta ads",
    icon: MetaIcon,
  },
  google_ads: {
    id: "google_ads",
    name: "Google Ads",
    nameLong: "Google Ads",
    icon: GoogleAdsIcon,
  },
  tiktok_ads: {
    id: "tiktok_ads",
    name: "TikTok Ads",
    nameLong: "TikTok Ads",
    icon: TikTokIcon,
  },
};

export const reportingIntegrationTypes = {
  slack: {
    id: "slack",
    name: "Slack",
    nameLong: "Slack",
    icon: SlackIcon,
  },
};

export const analyticsPages = {
  unitEconomics: {
    id: "unitEconomics",
    name: "Unit Economics",
    description: "Discover the profitability of your business",
    content: <UnitEconomics />,
  },
  cohortAnalytics: {
    id: "cohortAnalytics",
    name: "Cohorts",
    description: "Reach your customers at the right time",
    content: <CohortAnalytics />,
  },
  aovAnalytics: {
    id: "aovAnalytics",
    name: "AOV",
    description: "Find where your customers spend the most",
    content: <AovAnalytics />,
  },
  profitBreakeven: {
    id: "profitBreakeven",
    name: "Profit Breakeven",
    description: "Know when you'll start making a profit",
    content: <BreakEvenAnalysis />,
  },
};

export const financePages = {
  normalizedPL: {
    id: "normalizedPL",
    name: "Profit & Loss",
    description: "Understand the financial health of your business",
    content: <NormalizedPL />,
  },
};

export const marketingPages = {
  marketingActivity: {
    id: "marketingActivity",
    name: "Marketing Activity",
    description: "Track your marketing performance",
    content: <MarketingActivity />,
  },
  meta: {
    id: "meta",
    name: "Meta Ads",
    description: "Dial in your Meta marketing performance",
    content: (
      <MarketingActivity
        key="meta_ads"
        atom={metaMarketingActivityAtom}
        marketingIntegrationType={marketingIntegrationTypes.meta_ads}
      />
    ),
  },
  google: {
    id: "google",
    name: "Google Ads",
    description: "Dial in your Google marketing performance",
    content: (
      <MarketingActivity
        key="google_ads"
        atom={googleMarketingActivityAtom}
        marketingIntegrationType={marketingIntegrationTypes.google_ads}
      />
    ),
  },
};

export const dashboardPage = {
  id: "dashboard",
  name: "Insights",
  content: <Dashboard />,
};

export const allIntegrationTypes = {
  ...storeIntegrationTypes,
  ...marketingIntegrationTypes,
  ...reportingIntegrationTypes,
};

export const tutorialVideos = {
  unitEconomics: {
    title: "How to set nCAC targets",
    tabTitle: "nCAC Target",
    description:
      "How to use Unit Economics to find your breakeven spend and set the right new customer acquisition target.",
    src: "https://www.loom.com/embed/917cd4cf473942e2af98d717c1d3d08c?hideEmbedTopBar=true",
  },
  cohorts: {
    title: "How to use LTV data to set nCAC targets",
    tabTitle: "Cohort LTV",
    description:
      "How to analyze cohort LTV and payback periods to set sustainable acquisition targets when first-purchase profitability isn’t the goal.",
    src: "https://www.loom.com/embed/b7710c8d15b647d380a172246a81dd08?hideEmbedTopBar=true",
  },
  breakeven: {
    title: "How to set ad spend budgets",
    tabTitle: "Ad Spend",
    description:
      "A walkthrough of the breakeven analysis tool to find your optimal ad spend, balance efficiency with fixed costs, and set sustainable budgets without overextending cash flow.",
    src: "https://www.loom.com/embed/ef6389ea6de64571912d795449988d90?hideEmbedTopBar=true",
  },
};
